import { Empty, Spin, Table, Tag, Tooltip } from "antd";
import { useQuery } from "@tanstack/react-query";
import medicalReviewApi from "@/services/medicalReviewApi";
import { fieldLabel } from "./fieldLabels";
import type { PolicyVersion } from "./medicalReview.types";

const STATUS_COLOR: Record<string, string> = {
  active: "green",
  superseded: "default",
  withdrawn: "error",
};

function describe(version: PolicyVersion): string {
  if (version.policy_kind === "sla") {
    const hours = version.config.hours;
    return typeof hours === "number" ? `${hours} hours` : "—";
  }
  const fields = version.config.material_fields;
  if (!Array.isArray(fields)) return "—";
  return fields.map((f) => fieldLabel(String(f))).join(", ") || "nothing";
}

export default function PolicyHistory({ trialId }: { trialId: string }) {
  const historyQuery = useQuery({
    queryKey: ["medical-review-policy-history", trialId],
    queryFn: () => medicalReviewApi.policyHistory(trialId),
  });

  if (historyQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spin />
      </div>
    );
  }

  const versions = historyQuery.data?.versions ?? [];
  if (historyQuery.isError || versions.length === 0) {
    return (
      <Empty
        description="No policy has been authored for this trial yet, so review runs on Vivo's built-in defaults."
        className="my-12"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 max-w-[80ch] text-xs text-[#556C75]">
        Every version ever in force, newest first. Versions are never edited — a
        change writes a new row — so a review recorded under an earlier version
        still resolves to exactly what that version said.
      </p>
      <Table<PolicyVersion>
        dataSource={versions}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ x: true }}
        columns={[
          {
            title: "Policy",
            dataIndex: "label",
            render: (label: string, row) => (
              <div className="flex items-center gap-2">
                <span className="text-[#2B4752]">{label}</span>
                <Tag color={row.scope === "trial" ? "blue" : undefined}>
                  {row.scope === "trial" ? "This trial" : "Tenant standard"}
                </Tag>
              </div>
            ),
          },
          {
            title: "Version",
            dataIndex: "version",
            render: (version: number, row) => (
              <span className="tabular-nums">
                v{version}{" "}
                <Tag color={STATUS_COLOR[row.status]}>{row.status}</Tag>
              </span>
            ),
          },
          {
            title: "In force",
            dataIndex: "config",
            render: (_: unknown, row) => (
              <span className="text-xs text-[#556C75]">{describe(row)}</span>
            ),
          },
          {
            title: "Effective from",
            dataIndex: "effective_from",
            render: (value: string) => (
              <span className="text-xs whitespace-nowrap text-[#556C75]">
                {new Date(value).toLocaleString()}
              </span>
            ),
          },
          {
            title: "Author",
            dataIndex: "authored_by",
            render: (author: string | null, row) =>
              row.migrated_from_rule ? (
                <Tooltip
                  title={`Carried over from Rule Studio rule ${row.migrated_from_rule} when review policy moved to its own table.`}
                >
                  <span className="text-xs text-[#8FA5A0]">migrated</span>
                </Tooltip>
              ) : (
                <span className="text-xs text-[#556C75]">{author ?? "—"}</span>
              ),
          },
          {
            title: "Reason",
            dataIndex: "reason_for_change",
            render: (reason: string) => (
              <span className="text-xs text-[#556C75]">
                {reason || (
                  <span className="text-[#8FA5A0]">first version</span>
                )}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}
